import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PauseIcon from "@mui/icons-material/Pause";
import SettingsIcon from "@mui/icons-material/Settings";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Confetti from "react-confetti";
import FALLBACK_JIGSAW_PIECE_PATHS from "../../admin/test/jigsawPiecePaths-8.json";
import ScreenshotBoard from "../../prototype/mobile-jigsaw/board/ScreenshotBoard";
import { BOARD_SIZE, PieceVisual, pieceVisualRenderSizePx } from "../../prototype/mobile-jigsaw/board/PieceVisual";
import { PIECE_SHINE_CLEANUP_MS } from "../../prototype/mobile-jigsaw/board/PieceLockShineOverlay";
import AnimationPiecesTray, { TRAY_REORG_MS } from "../../prototype/mobile-jigsaw-animation/components/AnimationPiecesTray";
import {
    animatePieceAlongCurve,
    boardHomeScreenRect,
    flyControlPoint,
} from "../../prototype/mobile-jigsaw-animation/lib/pieceFlyAnimation";
import { trayRegionSx } from "../../prototype/mobile-jigsaw/styles/mainStyles";
import { JIGSAW_MUSE_GOOGLE_PLAY_URL } from "../../config/nativeAppsVisibility";
import { usePagesTranslation } from "../locales";
import LandingPhoneStoreCta from "./LandingPhoneStoreCta";

const ACCENT = "#3BA666";
const FALLBACK_GRID = 8;
const CONFETTI_MS = 3200;
const COMPLETE_HOLD_MS = 4000;
const NO_PIECES = [];
const JIGSAW_APP_NAME = "Jigsaw Muse";
const JIGSAW_APP_ICON = `${process.env.PUBLIC_URL || ""}/ad/jigsaw-muse-app-icon-512.png`;

const toolbarIconSx = {
    color: ACCENT,
    p: "5px",
    minWidth: 32,
    minHeight: 32,
    width: 32,
    height: 32,
    "& .MuiSvgIcon-root": { fontSize: "1.15rem" },
};

function allPieceIndices(count) {
    return Array.from({ length: Math.max(0, count) }, (_, i) => i);
}

function shuffleInPlace(pieces) {
    for (let i = pieces.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        const swap = pieces[i];
        pieces[i] = pieces[j];
        pieces[j] = swap;
    }
    return pieces;
}

function shuffledPieceIndices(count) {
    return shuffleInPlace(allPieceIndices(count));
}

function isEdgePiece(pieceIndex, grid) {
    if (grid <= 1) return true;
    const col = pieceIndex % grid;
    const row = Math.floor(pieceIndex / grid);
    return row === 0 || col === 0 || row === grid - 1 || col === grid - 1;
}

function neighborPieceIndices(pieceIndex, grid) {
    const col = pieceIndex % grid;
    const row = Math.floor(pieceIndex / grid);
    const next = [];
    if (row > 0) next.push(pieceIndex - grid);
    if (row < grid - 1) next.push(pieceIndex + grid);
    if (col > 0) next.push(pieceIndex - 1);
    if (col < grid - 1) next.push(pieceIndex + 1);
    return next;
}

function placedSetFromRemaining(remaining, grid) {
    const remainingSet = new Set(remaining);
    const placed = new Set();
    const total = grid * grid;
    for (let i = 0; i < total; i += 1) {
        if (!remainingSet.has(i)) placed.add(i);
    }
    return placed;
}

/** Border first; then only pieces that touch the placed frame. Prefer a piece already on-screen. */
function pickNextHumanPiece(remaining, grid, trayRefs, scrollEl) {
    if (remaining.length === 0) return null;
    const placed = placedSetFromRemaining(remaining, grid);
    const edgeRemaining = remaining.filter((pieceIndex) => isEdgePiece(pieceIndex, grid));
    let candidates = edgeRemaining;
    if (candidates.length === 0) {
        candidates = remaining.filter((pieceIndex) =>
            neighborPieceIndices(pieceIndex, grid).some((neighbor) => placed.has(neighbor)),
        );
    }
    if (candidates.length === 0) {
        candidates = remaining;
    }
    if (scrollEl) {
        const visible = candidates.find((pieceIndex) => {
            const el = trayRefs[pieceIndex];
            return el && isPieceVisibleInTray(el, scrollEl);
        });
        if (visible != null) return visible;
        let best = candidates[0];
        let bestDist = Infinity;
        const tray = scrollEl.getBoundingClientRect();
        const mid = tray.left + tray.width / 2;
        for (let i = 0; i < candidates.length; i += 1) {
            const el = trayRefs[candidates[i]];
            if (!el) continue;
            const rect = el.getBoundingClientRect();
            const dist = Math.abs(rect.left + rect.width / 2 - mid);
            if (dist < bestDist) {
                bestDist = dist;
                best = candidates[i];
            }
        }
        return best;
    }
    return candidates[0];
}

function toLocalRect(rect, rootRect) {
    if (!rect || !rootRect) return null;
    return {
        left: rect.left - rootRect.left,
        top: rect.top - rootRect.top,
        width: rect.width,
        height: rect.height,
    };
}

function flyTiming() {
    return {
        pieceDurationMs: 900,
        gapMs: 80,
        initialDelayMs: 900,
        findMs: 280,
        alreadyVisibleMs: 80,
    };
}

function isPieceVisibleInTray(pieceEl, scrollEl) {
    if (!pieceEl || !scrollEl) return false;
    const piece = pieceEl.getBoundingClientRect();
    const tray = scrollEl.getBoundingClientRect();
    if (piece.width <= 1 || tray.width <= 1) return false;
    const overlap = Math.min(piece.right, tray.right) - Math.max(piece.left, tray.left);
    return overlap >= piece.width * 0.7;
}

function clampTrayScroll(scrollEl) {
    if (!scrollEl) return;
    const max = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    if (scrollEl.scrollLeft > max) {
        scrollEl.scrollLeft = max;
    }
}

function scrollTrayToPiece(pieceEl, scrollEl, behavior = "smooth") {
    if (!pieceEl || !scrollEl) return;
    const itemEl = pieceEl.parentElement || pieceEl;
    const piece = itemEl.getBoundingClientRect();
    const tray = scrollEl.getBoundingClientRect();
    const delta = piece.left + piece.width / 2 - (tray.left + tray.width / 2);
    const max = Math.max(0, scrollEl.scrollWidth - scrollEl.clientWidth);
    const nextLeft = Math.max(0, Math.min(max, scrollEl.scrollLeft + delta));
    scrollEl.scrollTo({ left: nextLeft, behavior });
}

function formatPlayClock(totalSeconds) {
    const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
}

/** Static Muse play chrome — same controls as the Android play toolbar. */
function LandingJigsawPlayToolbar({ clock = "00:00" }) {
    return (
        <Box
            sx={{
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.15,
                px: 0.5,
                py: 0.35,
                minHeight: 40,
                bgcolor: "rgba(255,255,255,0.82)",
                borderBottom: "1px solid rgba(15,23,42,0.08)",
            }}
        >
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <ArrowBackIcon />
            </IconButton>
            <Typography
                component="span"
                sx={{
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    letterSpacing: "0.02em",
                    minWidth: "2.55rem",
                    textAlign: "center",
                    color: "#0f1419",
                }}
            >
                {clock}
            </Typography>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <PauseIcon />
            </IconButton>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <CleaningServicesIcon />
            </IconButton>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <LightbulbOutlinedIcon />
            </IconButton>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <ZoomOutIcon />
            </IconButton>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <ZoomInIcon />
            </IconButton>
            <IconButton size="small" tabIndex={-1} aria-hidden sx={toolbarIconSx}>
                <SettingsIcon />
            </IconButton>
        </Box>
    );
}

/**
 * Android Jigsaw Muse play: start with every piece in the tray, fly each to home, then confetti in-phone.
 */
export default function LandingJigsawPlayPreview({
    imageUrl,
    grid = 0,
    paths = null,
    boardPieceIndices = NO_PIECES,
    trayPieceIndices = NO_PIECES,
    svgPathFallback = null,
    active = true,
    onBusyChange,
    onPlayComplete,
}) {
    const { t } = usePagesTranslation();
    const [boardScale, setBoardScale] = useState(1);
    const [frameSize, setFrameSize] = useState({ w: 320, h: 480 });
    const [placedFromTray, setPlacedFromTray] = useState([]);
    const [hiddenTrayPieces, setHiddenTrayPieces] = useState([]);
    const [flyingFrame, setFlyingFrame] = useState(null);
    const [flyPieceIndex, setFlyPieceIndex] = useState(null);
    const [layoutReady, setLayoutReady] = useState(false);
    const [animationComplete, setAnimationComplete] = useState(false);
    const [confettiActive, setConfettiActive] = useState(false);
    const [elapsedSec, setElapsedSec] = useState(0);
    const [focusPieceIndex, setFocusPieceIndex] = useState(null);
    const [imageReady, setImageReady] = useState(false);
    const [lockShineByPieceIndex, setLockShineByPieceIndex] = useState({});

    const rootRef = useRef(null);
    const boardInnerRef = useRef(null);
    const trayPieceRefs = useRef({});
    const trayScrollRef = useRef(null);
    const cancelFlyRef = useRef(null);
    const flyQueueRef = useRef([]);
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const onPlayCompleteRef = useRef(onPlayComplete);
    onPlayCompleteRef.current = onPlayComplete;

    const boardImageUrl = useMemo(() => {
        const url = String(imageUrl || "").trim();
        if (!url) return "";
        if (/jigsaw-muse-feature-graphic/i.test(url) || /jigsaw-muse-app-icon/i.test(url)) {
            return "";
        }
        return url;
    }, [imageUrl]);

    useEffect(() => {
        if (!boardImageUrl) {
            setImageReady(false);
            return undefined;
        }
        let cancelled = false;
        setImageReady(false);
        const img = new Image();
        const markReady = () => {
            if (!cancelled) setImageReady(true);
        };
        img.onload = markReady;
        img.onerror = markReady;
        img.src = boardImageUrl;
        if (img.complete && img.naturalWidth > 0) {
            markReady();
        }
        return () => {
            cancelled = true;
        };
    }, [boardImageUrl]);

    const puzzleReady = Boolean(boardImageUrl) && imageReady;

    const pieceList = Array.isArray(paths) ? paths : paths?.pieces;
    const hasCutPieces = Boolean(boardImageUrl && grid > 0 && Array.isArray(pieceList) && pieceList.length > 0);
    const playGrid = hasCutPieces ? grid : FALLBACK_GRID;
    const playPaths = hasCutPieces ? pieceList : FALLBACK_JIGSAW_PIECE_PATHS;
    const startTray = useMemo(
        () => shuffledPieceIndices(playGrid * playGrid),
        [playGrid, boardImageUrl],
    );
    const startBoard = NO_PIECES;

    const flyQueue = useMemo(
        () => shuffledPieceIndices(playGrid * playGrid),
        [playGrid, boardImageUrl],
    );
    const flyKey = flyQueue.join(",");

    const visibleBoardPieces = useMemo(
        () => [...startBoard, ...placedFromTray],
        [startBoard, placedFromTray],
    );

    const pieceNativePx = useMemo(
        () => (playGrid > 0 ? pieceVisualRenderSizePx(BOARD_SIZE / playGrid) : 0),
        [playGrid],
    );

    const setTrayPieceRef = useCallback((pieceIndex, el) => {
        if (el) {
            trayPieceRefs.current[pieceIndex] = el;
        } else {
            delete trayPieceRefs.current[pieceIndex];
        }
    }, []);

    const resetPlay = useCallback(() => {
        cancelFlyRef.current?.();
        cancelFlyRef.current = null;
        flyQueueRef.current = [];
        setPlacedFromTray([]);
        setHiddenTrayPieces([]);
        setFlyingFrame(null);
        setFlyPieceIndex(null);
        setFocusPieceIndex(null);
        setLayoutReady(false);
        setAnimationComplete(false);
        setConfettiActive(false);
        setElapsedSec(0);
        setLockShineByPieceIndex({});
    }, []);

    useLayoutEffect(() => {
        const el = rootRef.current;
        if (!el) return undefined;
        const apply = () => {
            setFrameSize({
                w: Math.max(1, el.clientWidth),
                h: Math.max(1, el.clientHeight),
            });
        };
        apply();
        const observer = new ResizeObserver(apply);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!active || !puzzleReady) {
            resetPlay();
            return undefined;
        }
        resetPlay();
        const timer = window.setTimeout(() => setLayoutReady(true), 160);
        return () => {
            window.clearTimeout(timer);
            cancelFlyRef.current?.();
            cancelFlyRef.current = null;
        };
    }, [active, puzzleReady, playGrid, flyKey, resetPlay]);

    useEffect(() => {
        if (!active) return undefined;
        onBusyChangeRef.current?.(true);
        return () => onBusyChangeRef.current?.(false);
    }, [active]);

    useEffect(() => {
        if (!active || !layoutReady || !puzzleReady || flyQueue.length === 0) return undefined;

        flyQueueRef.current = [...flyQueue];
        const { pieceDurationMs, gapMs, initialDelayMs } = flyTiming();
        const timers = [];
        let rafId = 0;
        let cancelled = false;

        const wait = (ms) =>
            new Promise((resolve) => {
                const id = window.setTimeout(resolve, ms);
                timers.push(id);
            });

        const waitUntilVisible = (pieceEl, scrollEl, timeoutMs) =>
            new Promise((resolve) => {
                const started = performance.now();
                const tick = () => {
                    if (cancelled) {
                        resolve(false);
                        return;
                    }
                    if (isPieceVisibleInTray(pieceEl, scrollEl)) {
                        resolve(true);
                        return;
                    }
                    if (performance.now() - started >= timeoutMs) {
                        resolve(false);
                        return;
                    }
                    rafId = window.requestAnimationFrame(tick);
                };
                rafId = window.requestAnimationFrame(tick);
            });

        const waitForTrayReorg = async () => {
            await new Promise((resolve) => {
                rafId = window.requestAnimationFrame(() => {
                    rafId = window.requestAnimationFrame(() => {
                        clampTrayScroll(trayScrollRef.current);
                        resolve();
                    });
                });
            });
            await wait(TRAY_REORG_MS);
            clampTrayScroll(trayScrollRef.current);
        };

        const revealInTray = async (pieceIndex) => {
            setFocusPieceIndex(pieceIndex);
            const scrollEl = trayScrollRef.current;
            clampTrayScroll(scrollEl);
            for (let attempt = 0; attempt < 20 && !cancelled; attempt += 1) {
                const fromEl = trayPieceRefs.current[pieceIndex];
                if (fromEl && scrollEl) {
                    const alreadyVisible = isPieceVisibleInTray(fromEl, scrollEl);
                    if (!alreadyVisible) {
                        scrollTrayToPiece(fromEl, scrollEl, attempt === 0 ? "smooth" : "auto");
                        await waitUntilVisible(fromEl, scrollEl, attempt === 0 ? 900 : 240);
                    }
                    const shown = trayPieceRefs.current[pieceIndex];
                    if (shown && isPieceVisibleInTray(shown, scrollEl)) {
                        return shown;
                    }
                }
                await wait(50);
            }
            const fromEl = trayPieceRefs.current[pieceIndex];
            return fromEl && scrollEl && isPieceVisibleInTray(fromEl, scrollEl) ? fromEl : null;
        };

        const flyNext = async () => {
            if (cancelled) return;
            clampTrayScroll(trayScrollRef.current);
            const pieceIndex = pickNextHumanPiece(
                flyQueueRef.current,
                playGrid,
                trayPieceRefs.current,
                trayScrollRef.current,
            );
            if (pieceIndex == null) {
                setFocusPieceIndex(null);
                setAnimationComplete(true);
                return;
            }

            const fromEl = await revealInTray(pieceIndex);
            if (cancelled) return;

            const rootEl = rootRef.current;
            const boardEl = boardInnerRef.current;
            const toViewport = boardHomeScreenRect(pieceIndex, playGrid, boardEl);
            const rootRect = rootEl?.getBoundingClientRect();
            const toRect = toLocalRect(toViewport, rootRect);

            if (!fromEl || !toRect || !rootRect) {
                timers.push(window.setTimeout(flyNext, 80));
                return;
            }

            const queueAt = flyQueueRef.current.indexOf(pieceIndex);
            if (queueAt >= 0) {
                flyQueueRef.current.splice(queueAt, 1);
            }
            const fromRect = toLocalRect(fromEl.getBoundingClientRect(), rootRect);
            const control = flyControlPoint(fromRect, toRect);

            setFocusPieceIndex(null);
            setHiddenTrayPieces((prev) =>
                prev.includes(pieceIndex) ? prev : [...prev, pieceIndex],
            );
            setFlyPieceIndex(pieceIndex);
            setFlyingFrame(fromRect);

            cancelFlyRef.current?.();
            cancelFlyRef.current = animatePieceAlongCurve({
                fromRect,
                toRect,
                control,
                durationMs: pieceDurationMs,
                constantSize: false,
                onFrame: (frame) => setFlyingFrame(frame),
                onDone: () => {
                    setFlyPieceIndex(null);
                    setFlyingFrame(null);
                    setPlacedFromTray((prev) =>
                        prev.includes(pieceIndex) ? prev : [...prev, pieceIndex],
                    );
                    const shineKey = `${pieceIndex}-${Date.now()}`;
                    setLockShineByPieceIndex((prev) => ({ ...prev, [pieceIndex]: shineKey }));
                    timers.push(
                        window.setTimeout(() => {
                            setLockShineByPieceIndex((prev) => {
                                if (prev[pieceIndex] !== shineKey) return prev;
                                const next = { ...prev };
                                delete next[pieceIndex];
                                return next;
                            });
                        }, PIECE_SHINE_CLEANUP_MS),
                    );
                    const takeNext = async () => {
                        if (cancelled) return;
                        await waitForTrayReorg();
                        if (cancelled) return;
                        await wait(gapMs);
                        if (!cancelled) flyNext();
                    };
                    takeNext();
                },
            });
        };

        timers.push(window.setTimeout(flyNext, initialDelayMs));
        return () => {
            cancelled = true;
            timers.forEach((id) => window.clearTimeout(id));
            if (rafId) window.cancelAnimationFrame(rafId);
            cancelFlyRef.current?.();
            cancelFlyRef.current = null;
        };
    }, [active, layoutReady, puzzleReady, flyQueue, playGrid]);

    useEffect(() => {
        if (!active || !puzzleReady || !layoutReady) {
            return undefined;
        }
        if (animationComplete) {
            return undefined;
        }
        const startedAt = Date.now();
        setElapsedSec(0);
        const tick = window.setInterval(() => {
            setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
        }, 250);
        return () => window.clearInterval(tick);
    }, [active, puzzleReady, layoutReady, animationComplete]);

    useEffect(() => {
        if (!active || !animationComplete) return undefined;
        setConfettiActive(true);
        const confettiTimer = window.setTimeout(() => setConfettiActive(false), CONFETTI_MS);
        const busyTimer = window.setTimeout(
            () => {
                onBusyChangeRef.current?.(false);
                onPlayCompleteRef.current?.();
            },
            CONFETTI_MS + COMPLETE_HOLD_MS,
        );
        return () => {
            window.clearTimeout(confettiTimer);
            window.clearTimeout(busyTimer);
        };
    }, [active, animationComplete]);

    return (
        <Box
            ref={rootRef}
            sx={{
                position: "relative",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                bgcolor: puzzleReady ? "#c5d5ce" : "#0b1220",
                pointerEvents: "none",
                overflow: "hidden",
                pt: puzzleReady ? "36px" : 0,
                boxSizing: "border-box",
            }}
        >
            {!puzzleReady ? (
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 2,
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            fontWeight: 700,
                            fontSize: "1rem",
                            letterSpacing: "0.04em",
                            color: "rgba(226, 232, 240, 0.92)",
                            textAlign: "center",
                        }}
                    >
                        {t("landingApp.hero-phone-loading", "Loading...")}
                    </Typography>
                </Box>
            ) : (
                <>
            <LandingJigsawPlayToolbar clock={formatPlayClock(elapsedSec)} />
            <Box
                sx={{
                    flex: "1 1 auto",
                    minHeight: 0,
                    px: 0.75,
                    pt: 0.2,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                }}
            >
                <Box sx={{ width: "100%", maxHeight: "100%", aspectRatio: "1 / 1" }}>
                    {boardImageUrl ? (
                        <ScreenshotBoard
                            boardInnerRef={boardInnerRef}
                            grid={playGrid}
                            imageUrl={boardImageUrl}
                            paths={playPaths}
                            boardPieceIndices={visibleBoardPieces}
                            svgPathFallback={svgPathFallback}
                            onScaleChange={setBoardScale}
                            fullBleed
                            showDieLines={!animationComplete}
                            showFullImage={animationComplete}
                            fillHeight
                            lockShineByPieceIndex={lockShineByPieceIndex}
                        />
                    ) : null}
                </Box>
            </Box>
            {boardImageUrl && !animationComplete ? (
                <Box sx={{ ...trayRegionSx, py: 0.5, pb: 0.75, flexShrink: 0 }}>
                    <AnimationPiecesTray
                        pieceIndices={startTray}
                        hiddenPieceIndices={hiddenTrayPieces}
                        getPieceRef={setTrayPieceRef}
                        scrollContainerRef={trayScrollRef}
                        focusPieceIndex={focusPieceIndex}
                        imageUrl={boardImageUrl}
                        grid={playGrid}
                        paths={playPaths}
                        boardScale={boardScale}
                    />
                </Box>
            ) : (
                <Box
                    sx={{
                        ...trayRegionSx,
                        py: 0.85,
                        pb: 1,
                        minHeight: 64,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                    }}
                >
                    {animationComplete ? (
                        <LandingPhoneStoreCta
                            iconUrl={JIGSAW_APP_ICON}
                            appName={JIGSAW_APP_NAME}
                            playUrl={JIGSAW_MUSE_GOOGLE_PLAY_URL}
                            ariaLabel={t(
                                "landing.jigsaw-google-play-badge-aria",
                                "Get Jigsaw Muse on Google Play (opens in a new tab)",
                            )}
                            tone="light"
                        />
                    ) : null}
                </Box>
            )}

            {flyPieceIndex != null && flyingFrame && pieceNativePx > 0 ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        left: flyingFrame.left,
                        top: flyingFrame.top,
                        width: flyingFrame.width,
                        height: flyingFrame.height,
                        zIndex: 8,
                        pointerEvents: "none",
                        filter: "drop-shadow(0 6px 14px rgba(15,23,42,0.28))",
                    }}
                >
                    <Box
                        sx={{
                            transform: `scale(${flyingFrame.width / pieceNativePx})`,
                            transformOrigin: "top left",
                            lineHeight: 0,
                        }}
                    >
                        <PieceVisual
                            pieceIndex={flyPieceIndex}
                            imageUrl={boardImageUrl}
                            grid={playGrid}
                            paths={playPaths}
                            anchorToCell={false}
                        />
                    </Box>
                </Box>
            ) : null}

            {confettiActive ? (
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 12,
                        pointerEvents: "none",
                        overflow: "hidden",
                    }}
                >
                    <Confetti
                        width={frameSize.w}
                        height={frameSize.h}
                        recycle={false}
                        numberOfPieces={160}
                        gravity={0.32}
                        confettiSource={{ x: 0, y: 0, w: frameSize.w, h: 18 }}
                        style={{ pointerEvents: "none" }}
                    />
                </Box>
            ) : null}
                </>
            )}
        </Box>
    );
}
